import { memo, useMemo } from "react";
import { Box, Typography, List, ListItem, ListItemText } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";
import PropTypes from "prop-types";

const PrecautionsByCategory = memo(({ workPermit, t, theme }) => {
  const { token } = useSession();

  // Fetch precaution tree/categories
  const { data: precautionTree = [] } = useQuery({
    queryKey: ["precaution-tree"],
    queryFn: async () => {
      const response = await axios.get("/api/v1/metadata/precaution-tree", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: !!token,
  });

  // Build a map of precaution ID to category name for quick lookup
  const precautionToCategoryMap = useMemo(() => {
    const map = {};
    precautionTree.forEach((category) => {
      if (category.children && Array.isArray(category.children)) {
        category.children.forEach((precaution) => {
          map[precaution.id] = {
            categoryId: category.category_id,
            categoryName: category.category_name,
          };
        });
      }
    });
    return map;
  }, [precautionTree]);

  // Group precautions by category with deduplication
  const groupedPrecautions = useMemo(() => {
    if (!workPermit.hazards || workPermit.hazards.length === 0) {
      return {};
    }

    const grouped = {};
    const precautionIdsSeen = new Set();

    workPermit.hazards.forEach((hazard) => {
      if (hazard.precautions && Array.isArray(hazard.precautions)) {
        hazard.precautions.forEach((precaution) => {
          // Only show precautions that are selected
          if (precaution.selected !== true) {
            return;
          }

          // Avoid duplicates
          if (!precautionIdsSeen.has(precaution.id)) {
            precautionIdsSeen.add(precaution.id);

            // Find category
            const categoryInfo = precautionToCategoryMap[precaution.id];
            const categoryName =
              categoryInfo?.categoryName ||
              t("documentDetail.otherCategory", "Other");

            if (!grouped[categoryName]) {
              grouped[categoryName] = [];
            }
            // Add a flag to mark if this precaution is not in current metadata
            grouped[categoryName].push({
              ...precaution,
              isLegacy: !categoryInfo, // Mark as legacy if not found in current metadata
            });
          }
        });
      }
    });

    return grouped;
  }, [workPermit.hazards, precautionToCategoryMap, t]);

  // Sort categories to ensure "Other" is always at the end
  const categories = useMemo(() => {
    const allCategories = Object.keys(groupedPrecautions);
    const otherCategoryName = t("documentDetail.otherCategory", "Other");

    // Separate "Other" category from the rest
    const regularCategories = allCategories.filter(
      (cat) => cat !== otherCategoryName
    );
    const otherCategory = allCategories.filter(
      (cat) => cat === otherCategoryName
    );

    // Return regular categories first, then "Other" at the end
    return [...regularCategories.sort(), ...otherCategory];
  }, [groupedPrecautions, t]);

  // Check if there are precautions in the "Other" category
  const hasOtherCategory = useMemo(() => {
    const otherCategoryName = t("documentDetail.otherCategory", "Other");
    return categories.includes(otherCategoryName);
  }, [categories, t]);

  if (categories.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t("documentDetail.noDataAvailable")}
        </Typography>
      </Box>
    );
  }

  const otherCategoryName = t("documentDetail.otherCategory", "Other");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {categories.map((categoryName) => {
        const isOtherCategory = categoryName === otherCategoryName;

        return (
          <Box key={categoryName}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                mb: 1,
              }}
            >
              {categoryName}
            </Typography>
            <List sx={{ p: 0, pl: 2 }}>
              {groupedPrecautions[categoryName].map((precaution) => (
                <ListItem
                  key={precaution.id}
                  sx={{
                    py: 0.75,
                    px: 0,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <ListItemText
                    primary={precaution.name}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: {
                        fontWeight: isOtherCategory ? 500 : 400,
                        textDecoration: isOtherCategory ? "underline" : "none",
                        textDecorationColor: isOtherCategory
                          ? alpha(theme.palette.warning.main, 0.6)
                          : "transparent",
                        textDecorationThickness: "2px",
                        textUnderlineOffset: "3px",
                        color: theme.palette.text.primary,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );
      })}
      {hasOtherCategory && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 1,
            fontStyle: "italic",
            px: 1,
            py: 0.5,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            borderLeft: `3px solid ${theme.palette.info.main}`,
            borderRadius: 1,
          }}
        >
          {t(
            "documentDetail.precautionsNote",
            "The precautions listed reflect the precautions planned at the date of permit request."
          )}
        </Typography>
      )}
    </Box>
  );
});

PrecautionsByCategory.displayName = "PrecautionsByCategory";

PrecautionsByCategory.propTypes = {
  workPermit: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

export default PrecautionsByCategory;
